import { useEffect, useRef, useState, useCallback } from "react";

// Tunables for the gesture feel
const THRESHOLD = 120;  // px the user must pull before release triggers a refresh
const MAX_PULL = 160;   // px cap on how far the indicator can be dragged
const RESISTANCE = 1.5; // higher = harder to pull (rubber-band feel)
const MAX_ROTATION = 220; // deg of clockwise rotation reached at THRESHOLD

const getScrollTop = (scrollRef) =>
	scrollRef?.current ? scrollRef.current.scrollTop : (window.scrollY || document.documentElement.scrollTop);

const isScrolledDown = (target) => {
	if ((window.scrollY || document.documentElement.scrollTop || document.body.scrollTop) > 0) {
		return true;
	}
	let el = target;
	while (el && el !== document.body && el !== document.documentElement) {
		if (el.scrollTop > 0) {
			return true;
		}
		el = el.parentElement;
	}
	return false;
};

// Dialogs/Drawers/Sheets (Radix + vaul) render their content with role="dialog", vaul
// drawers additionally carry data-vaul-drawer, and both libraries mark their full-screen
// backdrop too (data-vaul-overlay / data-slot="dialog-overlay"). Checking the whole
// document rather than just the touch target's ancestry also catches touches that start
// on the backdrop itself (e.g. dragging above an open drawer's card) - anywhere a modal
// is open, the page behind it shouldn't also react to the gesture.
const hasOpenModal = () =>
	!!document.querySelector('[role="dialog"], [data-vaul-drawer], [data-vaul-overlay], [data-slot="dialog-overlay"]');

/**
 * Attaches a native-feeling pull-to-refresh gesture to a scrollable container
 * (or to the window, when `scrollRef` is omitted/null - for pages that scroll
 * at the document level rather than in their own overflow container).
 * All drag-time DOM updates go straight through refs (no React re-render per
 * touchmove) so the gesture stays smooth even on low-end mobile devices.
 *
 * @param {React.RefObject<HTMLElement>|null} scrollRef - the scrollable element to watch, or null/undefined to watch window scroll
 * @param {() => (void|Promise)} onRefresh - called when the user releases past the threshold
 * @param {{ disabled?: boolean }} options
 * @returns {{ indicatorRef: React.RefObject, isRefreshing: boolean }}
 */
export default function usePullToRefresh(scrollRef, onRefresh, { disabled = false } = {}) {
	const indicatorRef = useRef(null);
	const iconRef = useRef(null);
	const [isRefreshing, setIsRefreshing] = useState(false);

	const startY = useRef(0);
	const pulling = useRef(false);
	const dragged = useRef(false);
	const lastDist = useRef(0);

	const onRefreshRef = useRef(onRefresh);
	onRefreshRef.current = onRefresh;

	// Rotates the icon clockwise as the user pulls down, and spins it back
	// counter-clockwise to 0 when the pull is released/cancelled.
	const paint = useCallback((dist, animate) => {
		lastDist.current = dist;
		const el = indicatorRef.current;
		if (el) {
			el.style.transition = animate ? "transform 0.25s ease, opacity 0.25s ease" : "none";
			el.style.transform = `translateY(${dist}px)`;
			el.style.opacity = dist > 0 ? String(Math.min(dist / THRESHOLD, 1)) : "0";
		}
		const icon = iconRef.current;
		if (icon) {
			const rotation = Math.min(dist / THRESHOLD, 1.4) * MAX_ROTATION;
			icon.style.transition = animate ? "transform 0.3s ease" : "none";
			icon.style.transform = `rotate(${rotation}deg)`;
		}
	}, []);

	useEffect(() => {
		if (disabled) return undefined;
		// scrollRef omitted entirely -> watch window scroll. scrollRef provided
		// but not yet mounted (e.g. a conditionally-rendered tab) -> skip until
		// it is, rather than silently falling back to window.
		const usingWindow = !scrollRef;
		const target = usingWindow ? window : scrollRef.current;
		if (!target) return undefined;

		const handleTouchStart = (e) => {
			if (isRefreshing) return;
			if (isScrolledDown(e.target) || hasOpenModal()) {
				pulling.current = false;
				return;
			}
			startY.current = e.touches[0].clientY;
			pulling.current = true;
			dragged.current = false;
		};

		const handleTouchMove = (e) => {
			if (!pulling.current || isRefreshing) return;
			const dy = e.touches[0].clientY - startY.current;
			if (dy <= 0 || isScrolledDown(e.target) || hasOpenModal()) {
				pulling.current = false;
				paint(0, false);
				return;
			}
			dragged.current = true;
			// Only hijack the touch once we're actually pulling, so normal
			// scrolling elsewhere on the page is never blocked.
			e.preventDefault();
			const dist = Math.min(dy / RESISTANCE, MAX_PULL);
			paint(dist, false);
		};

		const handleTouchEnd = () => {
			if (!pulling.current) return;
			pulling.current = false;
			const shouldRefresh = dragged.current && lastDist.current >= THRESHOLD;
			dragged.current = false;

			if (shouldRefresh) {
				setIsRefreshing(true);
				paint(THRESHOLD, true);
				Promise.resolve()
					.then(() => onRefreshRef.current?.())
					.catch((err) => console.error("Pull-to-refresh handler failed", err))
					.finally(() => {
						paint(0, true);
						setIsRefreshing(false);
					});
			} else {
				paint(0, true);
			}
		};

		target.addEventListener("touchstart", handleTouchStart, { passive: true });
		target.addEventListener("touchmove", handleTouchMove, { passive: false });
		target.addEventListener("touchend", handleTouchEnd, { passive: true });
		target.addEventListener("touchcancel", handleTouchEnd, { passive: true });

		return () => {
			target.removeEventListener("touchstart", handleTouchStart);
			target.removeEventListener("touchmove", handleTouchMove);
			target.removeEventListener("touchend", handleTouchEnd);
			target.removeEventListener("touchcancel", handleTouchEnd);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scrollRef, scrollRef?.current, disabled, isRefreshing, paint]);

	return { indicatorRef, iconRef, isRefreshing };
}
